import { useState } from "react";
import API from "../config/api.js";
import { useAuth } from "../context/AuthContext";

export default function VideoCard({
  video,
  isPlaying = false,
  onTogglePreview,
  onVideoEnded,
  onUsed,
  onFavorite,
  isMyMedia = false,
  onRemove,
}) {
  const { subscription } = useAuth();

  const [isFavorite, setIsFavorite] = useState(video.idvideofavorite !== null);
  const [isUsed, setIsUsed]         = useState(video.idvideoused !== null);
  const [isRemoving, setIsRemoving] = useState(false);

  const isUserOwner = !!isMyMedia;

  const getAbsoluteUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API.baseUrl}/${path}`;
  };

  const getImageUrl = (path, size) => {
    if (!path) return "/assets/img/260x462.png";
    return getAbsoluteUrl(path.replace("original.jpg", size));
  };

  const videoFileUrl = video.file || "";
  const image           = getImageUrl(video.image, "1080x1920.jpg");
  const imageBackground = getImageUrl(video.image, "260x462.jpg");
  const videoSrc        = getAbsoluteUrl(videoFileUrl);

  const hasSubscription   = !!subscription;
  const isAvailableForFree = Number(video.availableforfree) === 1;
  const canAccessContent  = hasSubscription || isAvailableForFree || isUserOwner;

  const canDownload = canAccessContent && !!videoSrc;

  const itemSource = video.iduser ? "user" : "platform";

  const handlePreview = (e) => {
    e.preventDefault();
    if (!videoSrc) return;
    onTogglePreview?.(video.idvideo);
  };

  const handleDraft = () => {
    if (!video.iduser) {
      window._draft_prompt_clear?.();
    }

    window._draft_prompt_item_add?.(
      "video",
      video.idvideo,
      videoFileUrl,
      true,
      itemSource
    );
  };

  const handleUsed = async () => {
    const newUsed = !isUsed;
    setIsUsed(newUsed);

    try {
      const formData = new FormData();
      formData.append("idvideo", video.idvideo);

      const res = await fetch(`${API.videos}?used`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const status = await res.json();
      setIsUsed(status === true);
      onUsed?.(video.idvideo, status === true);
    } catch (err) {
      setIsUsed(!newUsed);
    }
  };

  const handleFavorite = async () => {
    const newFav = !isFavorite;
    setIsFavorite(newFav);

    try {
      const formData = new FormData();
      formData.append("idvideo", video.idvideo);

      const res = await fetch(`${API.videos}?favorite`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const status = await res.json();
      setIsFavorite(status === true);
      onFavorite?.(video.idvideo, status === true);
    } catch (err) {
      setIsFavorite(!newFav);
    }
  };

  const handleRemove = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isUserOwner) return;

    if (window.confirm && !window.confirm("Remove this video?")) {
      return;
    }

    try {
      setIsRemoving(true);

      if (window.$) {
        window.$("#preloader").show();
        window.$("#preloader .title").html("Removing...");
      }

      const formData = new FormData();
      formData.append("idvideo", video.idvideo);

      const res = await fetch(`${API.drafts}?removeVideo`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const result  = await res.text();
      const success = result === "true" || result === "1" || result === "ok" || result === "success";

      if (!res.ok || !success) {
        throw new Error("Erro ao remover video");
      }

      window._alert?.("success", "Video removed");
      onRemove?.(video.idvideo);
    } catch (err) {
      console.error("Erro ao remover video:", err);
      window._alert?.("danger", "Error removing video");
    } finally {
      setIsRemoving(false);

      if (window.$) {
        window.$("#preloader").hide();
        window.$("#preloader .title").html("");
      }
    }
  };

  return (
    <div
      className={`card suggested video border-0 video-${video.idvideo} rounded-4 animate__animated animate__fadeIn animate__slow`}
      data-idvideo={video.idvideo}
      data-url={videoFileUrl}
      data-iddraft=""
    >
      <div className="position-relative">
        <img
          src={image}
          style={{
            background: `url('${imageBackground}')`,
            backgroundSize: "cover",
          }}
          loading="lazy"
          alt=""
          className="img-fluid w-100 rounded-3 bg-loading"
          width="260"
          height="462"
          onError={(e) => {
            e.currentTarget.src = "/assets/img/260x462.png";
          }}
        />

        <div className="rounded position-absolute top-0 start-0 bottom-0 end-0 video-preview">
          {isPlaying && videoSrc && (
            <video
              className="w-100 h-100 rounded"
              autoPlay
              onEnded={onVideoEnded}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          )}
        </div>
      </div>

      {videoSrc && (
        <a href="#preview" style={{ zIndex: 4 }} onClick={handlePreview}>
          <div className="position-absolute top-50 start-50 translate-middle text-center video-play-pause">
            <div className="d-inline-flex align-items-center justify-content-center bg-white bg-opacity-25 backdrop-blur rounded-circle">
              <img
                src={
                  isPlaying
                    ? "/assets/img/icon/pause.png"
                    : "/assets/img/icon/play.png"
                }
                className="position-relative"
                width="70"
                height="70"
                alt="play"
              />
            </div>
          </div>
        </a>
      )}

      <div
        style={{ zIndex: 3 }}
        className="position-absolute top-0 end-0 p-2 text-white scheduled-date-time"
      ></div>

      <div
        style={{ zIndex: 4 }}
        className="position-absolute top-0 start-0 end-0 m-1 card-actions"
      >
        <div className="mt-2 px-2 py-1 action-buttons">
          <button type="button" className="btn use-button" onClick={handleDraft}>
            <img
              src="/assets/img/circle-plus.png"
              width="24"
              height="24"
              alt=""
            />
          </button>

          <div className="dropup dropend d-inline-block z-3 position-relative">
            <button
              className="btn dots-button"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <img src="/assets/img/dots.png" width="24" height="24" alt="" />
            </button>

            <ul className="dropdown-menu dropdown-menu-end">
              {canDownload && (
                <li>
                  <a
                    className="dropdown-item btn-video-download"
                    download={`Video ${video.title || "Video"}.mp4`}
                    href={videoSrc}
                  >
                    <i className="fi fi-rr-download me-2"></i> Download
                  </a>
                </li>
              )}

              <li>
                <button
                  className="dropdown-item btn-video-used"
                  onClick={handleUsed}
                >
                  {isUsed ? (
                    <>
                      <i className="fi fi-rr-checkbox me-2"></i> Used
                    </>
                  ) : (
                    <>
                      <i className="fi fi-rr-square me-2"></i> Mark as used
                    </>
                  )}
                </button>
              </li>

              <li>
                <button
                  className="dropdown-item btn-video-favorite"
                  onClick={handleFavorite}
                >
                  {isFavorite ? (
                    <>
                      <i className="fi fi-sr-heart me-2"></i> Favorited
                    </>
                  ) : (
                    <>
                      <i className="fi fi-rr-heart me-2"></i> Mark as favorite
                    </>
                  )}
                </button>
              </li>

              {isUserOwner && (
                <li>
                  <button
                    type="button"
                    className="dropdown-item text-danger"
                    onClick={handleRemove}
                    disabled={isRemoving}
                  >
                    <i className="fi fi-rr-trash me-2"></i>
                    {isRemoving ? "Removing..." : "Remove"}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div
        style={{ zIndex: 4 }}
        className="position-absolute bottom-0 start-0 end-0 p-2 m-1"
      >
        <h6 className="text-white" style={{ fontWeight: 700, fontSize: "12px" }}>
          Video{video.category_name ? ` • ${video.category_name}` : ""}
        </h6>

        <p className="card-text caption-content text-white">
          {video.caption || video.title || ""}
        </p>
      </div>
    </div>
  );
}